import React, { useState, useMemo } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { useMode } from '../../context/ModeContext';
import { useTheme } from '../../context/ThemeContext';
import { useChat } from '../../context/ChatContext.jsx';
import ModeSwitch from '../common/ModeSwitch';
import RoleBasedComponent from '../common/RoleBasedComponent';
import { 
  FiHome,
  FiUsers,
  FiFolder,
  FiCheckSquare,
  FiUserCheck,
  FiGrid,
  FiSettings,
  FiShield,
  FiDollarSign,
  FiBarChart2,
  FiChevronLeft,
  FiChevronRight,
  FiTrendingUp,
  FiTarget,
  FiAward,
  FiImage,
  FiActivity,
  FiBell,
  FiLayers,
  FiUserPlus,
  FiCalendar,
  FiChevronDown,
  FiMessageSquare
} from 'react-icons/fi';

/**
 * OrganizationSidebar - Role-based organization navigation
 * 
 * Features:
 * - Role-based menu items
 * - Organization branding integration
 * - Dynamic permissions
 * - Department/team context awareness
 */
const hexToRgba = (hex = '#000000', alpha = 1) => {
  const sanitized = hex.replace('#', '');
  if (sanitized.length !== 6) {
    return `rgba(0,0,0,${alpha})`;
  }
  const bigint = parseInt(sanitized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const OrganizationSidebar = ({ open, collapsed, toggleSidebar, toggleCollapse }) => {
  const { currentUser, getUserRoles } = useAuth();
  const { 
    currentOrganization, 
    hasPermission,
    isFullOrgAdmin,
    isLimitedOrgAdmin,
    isReadOnlyOrgAdmin
  } = useOrganization();
  const { getModeConfig } = useMode();
  const {
    theme,
    organizationBranding,
    getOrganizationBranding,
    isOrganizationThemed
  } = useTheme();
  const { totalUnread } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ department: true });

  const userRoles = getUserRoles();
  const modeConfig = getModeConfig();
  const branding = organizationBranding || getOrganizationBranding();
  const accentColor = branding?.primaryColor || theme.colors.primary;
  const sidebarBackground = isOrganizationThemed
    ? 'var(--org-sidebar-bg, var(--org-surface, #FFFFFF))'
    : branding.primaryColor
      ? `${branding.primaryColor}0D`
      : '#FFFFFF';
  const sidebarBorder = isOrganizationThemed ? 'var(--org-border, #E5E7EB)' : '#E5E7EB';
  const sidebarText = isOrganizationThemed ? 'var(--org-sidebar-text, #111827)' : '#111827';
  const sidebarMuted = isOrganizationThemed ? 'var(--org-sidebar-muted, #6B7280)' : '#6B7280';
  const navActiveBg = isOrganizationThemed ? 'rgba(255,255,255,0.12)' : hexToRgba(accentColor, 0.12);
  const navActiveBorder = isOrganizationThemed ? 'rgba(255,255,255,0.35)' : hexToRgba(accentColor, 0.35);
  const navHoverBg = isOrganizationThemed ? 'rgba(255,255,255,0.08)' : '#F3F4F6';

  const standardWidth = 256; // w-64
  const collapsedWidth = 64; // w-16

  // Get display name for role based on orgRole and access level
  const getRoleDisplayName = () => {
    if (userRoles.orgRole === 'org_admin') {
      if (isReadOnlyOrgAdmin) return 'Security Reviewer';
      if (isLimitedOrgAdmin) return 'Delegated Admin';
      return 'Full Admin';
    }
    switch (userRoles.orgRole) {
      case 'dept_manager': return 'Department Manager';
      case 'team_lead': return 'Team Lead';
      case 'member': return 'Member';
      default: return userRoles.orgRole?.replace('_', ' ') || 'Member';
    }
  };

  // Base navigation items available to all organization members
  const baseNavigation = [
    {
      name: 'Dashboard',
      path: '/org/dashboard',
      icon: FiHome,
      description: 'Your role-based dashboard',
      color: 'text-blue-600',
      permissions: ['canViewDashboard']
    },
    {
      name: 'Activity',
      path: '/org/activity',
      icon: FiActivity,
      description: 'Recent activity feed',
      color: 'text-teal-600',
      permissions: ['canViewDashboard']
    },
    {
      name: 'Notifications',
      path: '/org/notifications',
      icon: FiBell,
      description: 'Your notifications',
      color: 'text-amber-600',
      permissions: ['canViewDashboard']
    },
    {
      name: 'Messages',
      path: '/org/messages',
      icon: FiMessageSquare,
      description: 'Team conversations',
      color: 'text-indigo-600',
      permissions: ['canViewDashboard'],
      showUnread: true,
    }
  ];

  // Get the department path - direct to user's department if they have one
  const getDepartmentPath = useMemo(() => {
    if (currentUser?.departmentId) {
      // Navigate directly to user's department details
      return `/org/departments/${currentUser.departmentId}`;
    }
    // Fallback to departments list if user has no department
    return '/org/departments';
  }, [currentUser?.departmentId]);

  // Department section with nested navigation
  const departmentNavigation = {
    name: 'My Department',
    path: getDepartmentPath,
    icon: FiGrid,
    description: currentUser?.departmentId ? 'Your department workspace' : 'View all departments',
    color: 'text-cyan-600',
    permissions: ['canViewDepartments'],
    roles: ['org_admin', 'dept_manager', 'team_lead', 'member'],
    isExpandable: true,
    children: [
      {
        name: 'Projects',
        path: '/org/projects',
        icon: FiFolder,
        description: 'Department projects',
        color: 'text-green-600',
        permissions: ['canViewProjects']
      },
      {
        name: 'Tasks',
        path: '/org/tasks',
        icon: FiCheckSquare,
        description: 'Department tasks',
        color: 'text-orange-600',
        permissions: ['canViewTasks']
      },
      {
        name: 'Timeline',
        path: '/org/tasks/timeline',
        icon: FiCalendar,
        description: 'Gantt chart view',
        color: 'text-blue-600',
        // No permissions required - show based on roles only
        roles: ['org_admin', 'dept_manager', 'team_lead', 'member']
      },
      {
        name: 'Teams',
        path: '/org/teams',
        icon: FiLayers,
        description: 'Department teams',
        color: 'text-purple-600',
        permissions: ['canViewTeams']
      }
    ]
  };

  // Management navigation items (for managers and above)
  const managementNavigation = [
    {
      name: 'All Departments',
      path: '/org/departments',
      icon: FiGrid,
      description: 'View all departments',
      color: 'text-cyan-600',
      permissions: ['canViewDepartments'],
      roles: ['org_admin', 'dept_manager']
    },
    {
      name: 'Members',
      path: '/org/users',
      icon: FiUserCheck,
      description: 'View organization members',
      color: 'text-indigo-600',
      permissions: ['canViewUsers'],
      roles: ['org_admin', 'dept_manager', 'team_lead']
    },
    {
      name: 'Invite',
      path: '/org/invite',
      icon: FiUserPlus,
      description: 'Invite new members',
      color: 'text-rose-600',
      permissions: ['canInviteUsers'],
      roles: ['org_admin', 'dept_manager']
    },
    {
      name: 'Analytics',
      path: '/org/analytics',
      icon: FiBarChart2,
      description: 'Organization insights',
      color: 'text-pink-600',
      permissions: ['canViewAnalytics'],
      roles: ['org_admin', 'dept_manager', 'team_lead']
    }
  ];

  // Admin-only navigation items
  const adminNavigation = [
    {
      name: 'Settings',
      path: '/org/settings',
      icon: FiSettings,
      description: 'Organization settings',
      color: 'text-gray-600',
      permissions: ['canManageOrganization'],
      roles: ['org_admin']
    },
    {
      name: 'Security',
      path: '/org/security',
      icon: FiShield,
      description: 'Security & compliance',
      color: 'text-red-600',
      permissions: ['canManageSecurity'],
      roles: ['org_admin']
    },
    {
      name: 'Billing',
      path: '/org/billing',
      icon: FiDollarSign,
      description: 'Billing & subscription',
      color: 'text-emerald-600',
      permissions: ['canManageBilling'],
      roles: ['org_admin']
    },
    {
      name: 'Branding',
      path: '/org/branding',
      icon: FiImage,
      description: 'Custom branding',
      color: 'text-violet-600',
      permissions: ['canManageBranding'],
      roles: ['org_admin']
    }
  ];

  // Filter navigation items based on user permissions and roles
  // Permission check takes priority - if user has the permission, show the item
  const filterNavigationItems = (items) => {
    return items.filter(item => {
      // Check permission requirements FIRST (most important)
      // If user has the required permission, show the item regardless of role
      if (item.permissions && item.permissions.length > 0) {
        const hasRequiredPermission = item.permissions.some(permission => 
          hasPermission(permission)
        );
        if (hasRequiredPermission) return true;  // Permission grants access
      }
      
      // If no permissions defined or permission check failed, fall back to role check
      if (item.roles && item.roles.length > 0) {
        const userOrgRole = userRoles.orgRole || currentUser?.orgRole;
        const userDeptRole = currentUser?.departmentRole;
        
        const hasRequiredRole = item.roles.some(role => {
          // Direct role match
          if (userOrgRole === role) return true;
          
          // Department role match (team_lead)
          if (role === 'team_lead' && userDeptRole === 'team_lead') return true;
          
          // Hierarchy: org_admin can access dept_manager and team_lead items
          if (userOrgRole === 'org_admin') return true;
          
          // Hierarchy: dept_manager can access team_lead items
          if (userOrgRole === 'dept_manager' && role === 'team_lead') return true;
          if (userDeptRole === 'dept_manager' && role === 'team_lead') return true;
          
          return false;
        });
        
        if (hasRequiredRole) return true;  // Role grants access
      }
      
      // No permissions or roles defined means it's visible to everyone
      if (!item.permissions?.length && !item.roles?.length) return true;

      return false;
    });
  };

  const visibleBaseNavigation = filterNavigationItems(baseNavigation);
  const visibleManagementNavigation = filterNavigationItems(managementNavigation);
  const visibleAdminNavigation = filterNavigationItems(adminNavigation);

  // Filter department children
  const visibleDepartmentChildren = filterNavigationItems(departmentNavigation.children || []);

  // Check if department section should be shown
  const showDepartmentSection = filterNavigationItems([departmentNavigation]).length > 0;

  // Toggle expanded section
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Check if a path is active (for expandable items)
  const isPathActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const NavItem = ({ item, section = 'main', isChild = false }) => (
    <NavLink
      to={item.path}
      onMouseEnter={() => setHoveredItem(item.name)}
      onMouseLeave={() => setHoveredItem(null)}
      className={({ isActive }) =>
        `org-nav-item group relative ${collapsed ? 'justify-center' : ''} ${isActive ? 'active' : ''} ${isChild ? 'pl-8' : ''}`
      }
    >
      {({ isActive }) => (
        <>
          <item.icon 
            className={`${isChild ? 'w-4 h-4' : 'w-5 h-5'} transition-colors`}
            style={{
              color: isActive ? `var(--org-primary, ${accentColor})` : sidebarMuted
            }} 
          />
          
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <span className={`font-medium ${isChild ? 'text-xs' : 'text-sm'}`}>{item.name}</span>
            </div>
          )}

          {item.showUnread && totalUnread > 0 && (
            <span
              className={`${collapsed ? 'absolute top-0 right-0' : 'ml-auto'} inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none`}
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}

          {/* Tooltip for collapsed state */}
          {collapsed && hoveredItem === item.name && (
            <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap z-50">
              <div className="font-medium">{item.name}</div>
              <div className="text-xs text-gray-300 mt-1">{item.description}</div>
              <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          )}
        </>
      )}
    </NavLink>
  );

  // Expandable navigation item with children
  const ExpandableNavItem = ({ item, childItems }) => {
    const isExpanded = expandedSections.department;
    const isActive = isPathActive(item.path);
    const hasActiveChild = childItems?.some(child => isPathActive(child.path));

    return (
      <div className="space-y-1">
        {/* Parent item - clickable to navigate */}
        <div className="relative">
          <NavLink
            to={item.path}
            onMouseEnter={() => setHoveredItem(item.name)}
            onMouseLeave={() => setHoveredItem(null)}
            className={`org-nav-item group relative ${collapsed ? 'justify-center' : ''} ${isActive || hasActiveChild ? 'active' : ''}`}
          >
            <item.icon 
              className="w-5 h-5 transition-colors"
              style={{
                color: isActive || hasActiveChild ? `var(--org-primary, ${accentColor})` : sidebarMuted
              }} 
            />
            
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm">{item.name}</span>
              </div>
            )}
          </NavLink>

          {/* Expand/collapse button */}
          {!collapsed && childItems && childItems.length > 0 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleSection('department');
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              style={{ color: sidebarMuted }}
            >
              <FiChevronDown 
                className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}

          {/* Tooltip for collapsed state */}
          {collapsed && hoveredItem === item.name && (
            <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap z-50">
              <div className="font-medium">{item.name}</div>
              <div className="text-xs text-gray-300 mt-1">{item.description}</div>
              <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          )}
        </div>

        {/* Children - shown when expanded */}
        {!collapsed && isExpanded && childItems && childItems.length > 0 && (
          <div className="ml-2 space-y-0.5 border-l-2 border-gray-200 dark:border-gray-700">
            {childItems.map((child) => (
              <NavItem key={child.name} item={child} isChild={true} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const NavSection = ({ title, items, collapsed }) => {
    if (items.length === 0) return null;
    
    return (
      <div className="space-y-1">
        {!collapsed && (
            <div className="px-3 py-2">
              <h3
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: sidebarMuted }}
              >
              {title}
            </h3>
          </div>
        )}
        {items.map((item) => (
          <NavItem key={item.name} item={item} />
        ))}
      </div>
    );
  };

  return (
    <div
      className={`${
        collapsed ? 'w-16' : 'w-64'
      } org-sidebar shadow-lg border-r transition-all duration-300 flex flex-col relative`}
      style={{
        width: collapsed ? collapsedWidth : standardWidth,
        background: sidebarBackground,
        borderColor: sidebarBorder,
        color: sidebarText,
        '--org-nav-hover-bg': navHoverBg,
        '--org-nav-active-bg': navActiveBg,
        '--org-nav-active-border': navActiveBorder
      }}
    >
      {/* Header with Organization Branding */}
      <div
        className="flex items-center justify-between p-4 border-b"
        style={{ borderColor: sidebarBorder }}
      >
        {!collapsed && (
          <div className="flex items-center space-x-3">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                backgroundColor: isOrganizationThemed ? `var(--org-primary, ${accentColor})` : branding.primaryColor || accentColor,
                backgroundImage: branding.logo ? `url(${branding.logo})` : undefined,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}
            >
              {!branding.logo && (
                <span className="text-white font-bold text-sm">
                  {currentOrganization?.name?.charAt(0) || 'O'}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-lg font-bold"
                style={{ color: sidebarText }}
              >
                {currentOrganization?.name || 'Organization'}
              </h1>
              <p className="text-xs capitalize" style={{ color: sidebarMuted }}>
                {getRoleDisplayName()}
              </p>
            </div>
          </div>
        )}
        
        <button
          onClick={toggleCollapse}
          className="p-1.5 rounded-lg transition-colors"
          style={{ color: sidebarMuted }}
        >
          {collapsed ? (
            <FiChevronRight className="w-4 h-4" style={{ color: sidebarMuted }} />
          ) : (
            <FiChevronLeft className="w-4 h-4" style={{ color: sidebarMuted }} />
          )}
        </button>
      </div>

      {/* Mode Switcher */}
      <div
        className="p-4 border-b"
        style={{ borderColor: sidebarBorder }}
      >
        <ModeSwitch variant={collapsed ? "compact" : "default"} showLabel={!collapsed} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-6 overflow-y-auto">
        {/* Base Navigation */}
        <NavSection 
          title="Workspace" 
          items={visibleBaseNavigation} 
          collapsed={collapsed}
        />

        {/* Department Section - Expandable with nested items */}
        {showDepartmentSection && (
          <div className="space-y-1">
            {!collapsed && (
              <div className="px-3 py-2">
                <h3
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: sidebarMuted }}
                >
                  Department
                </h3>
              </div>
            )}
            <ExpandableNavItem 
              item={departmentNavigation} 
              childItems={visibleDepartmentChildren}
            />
          </div>
        )}

        {/* Management Navigation */}
        {visibleManagementNavigation.length > 0 && (
          <NavSection 
            title="Management" 
            items={visibleManagementNavigation} 
            collapsed={collapsed}
          />
        )}

        {/* Admin Navigation */}
        {visibleAdminNavigation.length > 0 && (
          <NavSection 
            title="Administration" 
            items={visibleAdminNavigation} 
            collapsed={collapsed}
          />
        )}

        {/* Organization Stats */}
        {!collapsed && (
          <div 
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: isOrganizationThemed ? 'rgba(255,255,255,0.08)' : '#f8fafc',
              borderColor: sidebarBorder
            }}
          >
            <div className="text-xs font-medium mb-3" style={{ color: sidebarMuted }}>
              Organization Stats
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FiUsers className="w-3 h-3" style={{ color: accentColor }} />
                  <span className="text-xs" style={{ color: sidebarMuted }}>Total Members</span>
                </div>
                <span className="text-xs font-medium" style={{ color: sidebarText }}>
                  {currentOrganization?.memberCount || '0'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FiTarget className="w-3 h-3" style={{ color: accentColor }} />
                  <span className="text-xs" style={{ color: sidebarMuted }}>Active Projects</span>
                </div>
                <span className="text-xs font-medium" style={{ color: sidebarText }}>
                  {currentOrganization?.activeProjects || '0'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FiAward className="w-3 h-3" style={{ color: accentColor }} />
                  <span className="text-xs" style={{ color: sidebarMuted }}>Completion</span>
                </div>
                <span className="text-xs font-medium" style={{ color: sidebarText }}>
                  {currentOrganization?.completionRate || '0'}%
                </span>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* User Info */}
      {!collapsed && currentUser && (
        <div
          className="p-4 border-t"
          style={{ borderColor: sidebarBorder }}
        >
          <div className="flex items-center space-x-3">
            <img
              src={currentUser.profileImage || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.name)}&background=3b82f6&color=ffffff`}
              alt={currentUser.name}
              className="w-8 h-8 rounded-full"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: sidebarText }}>
                {currentUser.name}
              </p>
              <p className="text-xs truncate capitalize" style={{ color: sidebarMuted }}>
                {getRoleDisplayName()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mobile overlay */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleSidebar}
        />
      )}
    </div>
  );
};

export default OrganizationSidebar;
